import { useState, useEffect } from 'react'
import { Plus, Edit2, Trash2, Phone, Car, Zap, Receipt, Copy, ListOrdered } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { format, addYears } from 'date-fns'
import { driverApi, type DriverCreateRequest } from '../../api/drivers'
import { tenantApi } from '../../api'
import { Driver, DriverStatus, DriverApprovalStatus, Tenant } from '../../types'
import { useAuthStore } from '../../stores/authStore'
import TableLoading from '@/components/ui/TableLoading'
import DriverDrawer from '@/components/drivers/DriverDrawer'
import DriverExpensePanel from '@/components/drivers/DriverExpensePanel'
import DriverOrderModal from '@/components/drivers/DriverOrderModal'
import LeaveCalendar from '@/components/drivers/LeaveCalendar'
import { Button } from "@/components/ui/button"


const STATUS_LABELS: Record<DriverStatus, string> = {
  [DriverStatus.AVAILABLE]: '可用',
  [DriverStatus.BUSY]: '忙碌',
  [DriverStatus.OFFLINE]: '離線',
  [DriverStatus.ON_BREAK]: '休息',
}

const STATUS_COLORS: Record<DriverStatus, string> = {
  [DriverStatus.AVAILABLE]: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  [DriverStatus.BUSY]: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
  [DriverStatus.OFFLINE]: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300',
  [DriverStatus.ON_BREAK]: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
}

const APPROVAL_STATUS_LABELS: Record<DriverApprovalStatus, string> = {
  [DriverApprovalStatus.PENDING]: '待審核',
  [DriverApprovalStatus.APPROVED]: '已通過',
  [DriverApprovalStatus.REJECTED]: '已駁回',
}

const APPROVAL_STATUS_COLORS: Record<DriverApprovalStatus, string> = {
  [DriverApprovalStatus.PENDING]: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
  [DriverApprovalStatus.APPROVED]: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  [DriverApprovalStatus.REJECTED]: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
}

export default function DriversPage() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [drivers, setDrivers] = useState<Driver[]>([])
  const [loading, setLoading] = useState(true)
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [showOrderModal, setShowOrderModal] = useState(false)
  const [limit] = useState(10)
  const [statusFilter, setStatusFilter] = useState<DriverStatus | ''>('')
  const [copiedLinkId, setCopiedLinkId] = useState<string | null>(null)
  const [error, setError] = useState<string>('')
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [selectedDriver, setSelectedDriver] = useState<Driver | null>(null)
  const [tenants, setTenants] = useState<Tenant[]>([])
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null)
  const [showQuickCreate, setShowQuickCreate] = useState(false)
  const [quickText, setQuickText] = useState('')
  const [quickParsed, setQuickParsed] = useState<Partial<DriverCreateRequest> | null>(null)
  const [quickCreateLoading, setQuickCreateLoading] = useState(false)
  const [quickCreateError, setQuickCreateError] = useState('')
  const [expensePanelDriver, setExpensePanelDriver] = useState<Driver | null>(null)
  const [activeTab, setActiveTab] = useState<'list' | 'leaves'>('list')

  const COLORS = ['黑', '白', '銀', '灰', '紅', '藍', '綠', '黃', '棕', '咖啡', '珍珠', '深藍', '香檳']

  const parseDriverText = (text: string): Partial<DriverCreateRequest> => {
    // 支援 pipe 格式：姓名|電話|車號 或 姓名|電話|車號|車種
    const trimmed = text.trim()
    if (/^[^：:\n]+\|[^：:\n]+\|[^：:\n]+/.test(trimmed)) {
      const parts = trimmed.split('|').map(s => s.trim())
      const [pipeName, pipePhone, pipePlate, pipeVehicle = ''] = parts
      let vehicle_color = ''
      let vehicle_brand_model = pipeVehicle
      if (pipeVehicle) {
        const found = COLORS.find(c => pipeVehicle.startsWith(c))
        if (found) { vehicle_color = found; vehicle_brand_model = pipeVehicle.slice(found.length).trim() }
      }
      return { name: pipeName, phone: pipePhone, vehicle_plate: pipePlate, vehicle_brand_model, vehicle_color, vehicle_type: vehicle_brand_model || pipeVehicle, license_number: '待補', license_expiry: format(addYears(new Date(), 1), 'yyyy-MM-dd') }
    }

    const get = (patterns: RegExp[]) => {
      for (const re of patterns) {
        const m = text.match(re)
        if (m) return m[1].trim()
      }
      return ''
    }

    const name = get([/司機[：:]?\s*(.+)/,  /姓名[：:]?\s*(.+)/])
    const phone = get([/電話[：:]?\s*([\d\-]+)/, /手機[：:]?\s*([\d\-]+)/])
    const plate = get([/車號[：:]?\s*([A-Za-z0-9\-]+)/, /車牌[：:]?\s*([A-Za-z0-9\-]+)/])
    const vehicleRaw = get([/車種[：:]?\s*(.+)/, /車輛[：:]?\s*(.+)/])

    let vehicle_color = ''
    let vehicle_brand_model = vehicleRaw
    if (vehicleRaw) {
      const found = COLORS.find(c => vehicleRaw.startsWith(c))
      if (found) {
        vehicle_color = found
        vehicle_brand_model = vehicleRaw.slice(found.length).trim()
      }
    }

    const license_number = get([/駕照[：:]?\s*(.+)/, /駕照號碼[：:]?\s*(.+)/]) || '待補'
    const license_expiry = get([/到期[：:]?\s*(.+)/, /駕照到期[：:]?\s*(.+)/]) ||
      format(addYears(new Date(), 1), 'yyyy-MM-dd')

    return { name, phone, vehicle_plate: plate, vehicle_brand_model, vehicle_color, vehicle_type: vehicle_brand_model || vehicleRaw, license_number, license_expiry }
  }

  const handleQuickCreate = async () => {
    if (!quickParsed?.name || !quickParsed?.phone || !quickParsed?.vehicle_plate) {
      setQuickCreateError('請確認已解析出：司機姓名、電話、車號')
      return
    }
    setQuickCreateLoading(true)
    setQuickCreateError('')
    try {
      await driverApi.createDriver({
        name: quickParsed.name!,
        phone: quickParsed.phone!,
        vehicle_type: quickParsed.vehicle_type || quickParsed.vehicle_brand_model || '未指定',
        vehicle_plate: quickParsed.vehicle_plate!,
        vehicle_color: quickParsed.vehicle_color,
        vehicle_brand_model: quickParsed.vehicle_brand_model,
        license_number: quickParsed.license_number || '待補',
        license_expiry: quickParsed.license_expiry || format(addYears(new Date(), 1), 'yyyy-MM-dd'),
        ...(user?.role === 'super_admin' ? {} : { tenant_id: user?.tenant_id }),
      })
      setShowQuickCreate(false)
      setQuickText('')
      setQuickParsed(null)
      fetchDrivers()
    } catch (err: any) {
      setQuickCreateError(err?.response?.data?.detail || '建立失敗')
    } finally {
      setQuickCreateLoading(false)
    }
  }

  const fetchDrivers = async () => {
    try {
      setLoading(true)
      setError('')
      const data = await driverApi.getDrivers(
        page,
        limit,
        statusFilter || undefined,
      )
      setDrivers(data.items)
      setTotal(data.total)
    } catch (error: any) {
      console.error('Failed to fetch drivers:', error)
      if (error.response?.status === 401) {
        navigate('/login', { replace: true })
      } else {
        setError('載入司機資料失敗，請稍後再試')
      }
    } finally {
      setLoading(false)
    }
  }

  const fetchTenants = async () => {
    try {
      const data = await tenantApi.list({ limit: 100 })
      setTenants(data.items)
    } catch (error) {
      console.error('Failed to fetch tenants:', error)
    }
  }

  const handleOpenDrawer = async () => {
    await fetchTenants()
    setIsDrawerOpen(true)
  }

  // 檢查認證並加載數據
  useEffect(() => {
    if (!user) {
      navigate('/login', { replace: true })
      return
    }
    fetchDrivers()
  }, [user, page, limit, statusFilter, navigate])

  const handleCreate = async (data: DriverCreateRequest & { tenant_id?: string }) => {
    await driverApi.createDriver(data)
    setIsDrawerOpen(false)
    fetchDrivers()
  }

  const handleEdit = async (driver: Driver) => {
    setSelectedDriver(driver)
    await fetchTenants()
    setIsDrawerOpen(true)
  }

  const handleDelete = async (id: string) => {
    setShowDeleteConfirm(id)
  }

  const handleCopyLink = async (driver: Driver) => {
    if (!driver.sign_token) return
    const url = `${window.location.origin}/driver-sign/${driver.sign_token}`
    await navigator.clipboard.writeText(url)
    setCopiedLinkId(driver.id)
    setTimeout(() => setCopiedLinkId(null), 2000)
  }

  const confirmDelete = async (id: string) => {
    try {
      await driverApi.deleteDriver(id)
      setShowDeleteConfirm(null)
      fetchDrivers()
    } catch (error) {
      console.error('Failed to delete driver:', error)
    }
  }

  const handleUpdate = async (data: any) => {
    if (selectedDriver) {
      await driverApi.updateDriver(selectedDriver.id, data)
    }
    setIsDrawerOpen(false)
    setSelectedDriver(null)
    fetchDrivers()
  }

  if (!user) {
    return null
  }

  return (
    <div className="space-y-6 p-1.5 sm:p-6">
      {error && (
        <div className="rounded-lg bg-red-50 p-4 text-red-800 dark:bg-red-900 dark:text-red-200">
          {error}
        </div>
      )}

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">司機管理</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">管理司機資訊與狀態</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => { setQuickText(''); setQuickParsed(null); setQuickCreateError(''); setShowQuickCreate(true) }}
            className="border-orange-300 text-orange-600 hover:bg-orange-50 dark:border-orange-700 dark:text-orange-400 dark:hover:bg-orange-900/20"
          >
            <Zap className="h-4 w-4 mr-1.5" /> 快速新增
          </Button>
          <Button variant="outline" onClick={() => setShowOrderModal(true)}>
            <ListOrdered className="h-4 w-4 mr-1.5" /> 調整順序
          </Button>
          <Button onClick={handleOpenDrawer} className="bg-blue-600 hover:bg-blue-700 text-white">
            <Plus className="h-4 w-4 mr-1.5" /> 新增司機
          </Button>
        </div>
      </div>

      {/* Tab switcher */}
      <div className="flex gap-1 border-b border-border">
        <button
          onClick={() => setActiveTab('list')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors cursor-pointer ${
            activeTab === 'list'
              ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
          }`}
        >
          司機列表
        </button>
        <button
          onClick={() => setActiveTab('leaves')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors cursor-pointer ${
            activeTab === 'leaves'
              ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
          }`}
        >
          請假管理
        </button>
      </div>

      {activeTab === 'leaves' && (
        <div className="rounded-xl border border-border bg-card p-6">
          <LeaveCalendar />
        </div>
      )}

      {activeTab === 'list' && (
      <>
      <div className="flex flex-col gap-4 sm:flex-row">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as DriverStatus | '')}
          className="rounded-lg border border-input bg-background px-4 py-2 text-sm focus:ring-1 focus:ring-ring"
        >
          <option value="">所有狀態</option>
          <option value={DriverStatus.AVAILABLE}>可用</option>
          <option value={DriverStatus.BUSY}>忙碌</option>
          <option value={DriverStatus.OFFLINE}>離線</option>
          <option value={DriverStatus.ON_BREAK}>休息</option>
        </select>
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-card text-card-foreground shadow-sm">
        {/* Mobile Card View */}
        <div className="md:hidden divide-y divide-border">
          {loading ? (
            <div className="p-8 text-center text-muted-foreground">正在加載司機...</div>
          ) : drivers.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">找不到符合條件的司機</div>
          ) : (
            drivers.map((driver) => (
              <div key={driver.id} className="p-4 space-y-3 bg-card">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-300">
                      <Car className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="font-bold text-foreground">
                        {driver.driver_category === 'external' && <span className="text-purple-600 dark:text-purple-400">[外] </span>}
                        {driver.name}
                      </div>
                      <div className="text-xs text-muted-foreground">{driver.vehicle_plate}</div>
                    </div>
                  </div>
                  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_COLORS[driver.status]}`}>
                    {STATUS_LABELS[driver.status]}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-2 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    <span>{driver.phone}</span>
                  </div>
                  <div>
                    {driver.vehicle_type}
                  </div>
                  <div className="col-span-2 text-xs">
                    駕照到期: {format(new Date(driver.license_expiry), 'yyyy/MM/dd')}
                  </div>
                  <div className="col-span-2">
                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${APPROVAL_STATUS_COLORS[driver.approval_status]}`}>
                      {APPROVAL_STATUS_LABELS[driver.approval_status]}
                    </span>
                  </div>
                </div>

                <div className="flex gap-2 pt-2 border-t border-border">
                  <Button variant="ghost" size="sm" className="flex-1" onClick={() => handleEdit(driver)}>
                    <Edit2 className="w-4 h-4 mr-1" /> 編輯
                  </Button>
                  {driver.driver_category === 'external' && driver.sign_token && (
                    <Button variant="ghost" size="sm" className="flex-1 text-purple-600 hover:text-purple-700 hover:bg-purple-50" onClick={() => handleCopyLink(driver)}>
                      <Copy className="w-4 h-4 mr-1" /> {copiedLinkId === driver.id ? '已複製' : '連結'}
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" className="flex-1 text-orange-600 hover:text-orange-700 hover:bg-orange-50" onClick={() => setExpensePanelDriver(driver)}>
                    <Receipt className="w-4 h-4 mr-1" /> 支出
                  </Button>
                  <Button variant="ghost" size="sm" className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleDelete(driver.id)}>
                    <Trash2 className="w-4 h-4 mr-1" /> 刪除
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Desktop Table View */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-6 py-3 font-medium">司機資訊</th>
                <th className="px-6 py-3 font-medium">車輛資訊</th>
                <th className="px-6 py-3 font-medium">車輛檢驗日期</th>
                <th className="px-6 py-3 font-medium">狀態</th>
                <th className="px-6 py-3 font-medium">審核狀態</th>
                <th className="px-6 py-3 font-medium">聯絡電話</th>
                <th className="px-6 py-3 font-medium">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading ? (
                <TableLoading colSpan={6} message="正在加載司機..." />
              ) : drivers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <Car className="mx-auto h-12 w-12 text-muted-foreground/50" />
                    <p className="mt-2 text-sm text-muted-foreground">
                      找不到符合條件的司機
                    </p>
                  </td>
                </tr>
              ) : (
                drivers.map((driver) => (
                  <tr key={driver.id} className="hover:bg-muted/50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-300">
                          <Car className="h-5 w-5" />
                        </div>
                        <div>
                          <div className="font-medium text-foreground">
                            {driver.driver_category === 'external' && <span className="text-purple-600 dark:text-purple-400">[外] </span>}
                            {driver.name}
                          </div>
                          <div className="text-xs text-muted-foreground">ID: {driver.id.substring(0, 8)}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1">
                        <span className="font-medium text-foreground">
                          {driver.vehicle_type}
                        </span>
                        <span className="text-xs text-muted-foreground">{driver.vehicle_plate}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {driver.vehicle_inspection_date ? (() => {
                        const d = new Date(driver.vehicle_inspection_date)
                        const soon = new Date()
                        soon.setMonth(soon.getMonth() + 1)
                        const isExpiringSoon = d <= soon
                        return (
                          <span className={isExpiringSoon ? 'font-bold text-red-600 dark:text-red-400' : 'text-foreground'}>
                            {format(d, 'yyyy/MM/dd')}
                          </span>
                        )
                      })() : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_COLORS[driver.status]}`}>
                        {STATUS_LABELS[driver.status]}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${APPROVAL_STATUS_COLORS[driver.approval_status]}`}>
                        {APPROVAL_STATUS_LABELS[driver.approval_status]}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-foreground">
                        <Phone className="h-3 w-3" />
                        <span>{driver.phone}</span>
                      </div>
                    </td>
                     <td className="px-6 py-4">
                       <div className="flex items-center gap-2">
                         <Button variant="ghost" size="icon" onClick={() => handleEdit(driver)} className="h-8 w-8 text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20">
                           <Edit2 className="h-4 w-4" />
                         </Button>
                         {driver.driver_category === 'external' && driver.sign_token && (
                           <Button variant="ghost" size="icon" onClick={() => handleCopyLink(driver)} className="h-8 w-8 text-purple-600 hover:text-purple-700 hover:bg-purple-50 dark:hover:bg-purple-900/20" title={copiedLinkId === driver.id ? '已複製！' : '複製簽名連結'}>
                             <Copy className="h-4 w-4" />
                           </Button>
                         )}
                         <Button variant="ghost" size="icon" onClick={() => setExpensePanelDriver(driver)} className="h-8 w-8 text-orange-600 hover:text-orange-700 hover:bg-orange-50 dark:hover:bg-orange-900/20" title="支出記錄">
                           <Receipt className="h-4 w-4" />
                         </Button>
                         <Button
                           variant="ghost"
                           size="icon"
                           onClick={() => handleDelete(driver.id)}
                           className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                         >
                           <Trash2 className="h-4 w-4" />
                         </Button>
                       </div>
                     </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between border-t border-border bg-card px-4 py-3 sm:px-6">
          <div className="flex flex-1 justify-between sm:hidden">
            <Button
              variant="outline"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              size="sm"
            >
              上一頁
            </Button>
            <Button
              variant="outline"
              onClick={() => setPage((p) => p + 1)}
              disabled={drivers.length < limit}
              size="sm"
            >
              下一頁
            </Button>
          </div>
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-muted-foreground">
                顯示第 <span className="font-medium">{(page - 1) * limit + 1}</span> 到{' '}
                <span className="font-medium">{Math.min(page * limit, total)}</span> 筆，共{' '}
                <span className="font-medium">{total}</span> 筆
              </p>
            </div>
            <div>
              <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                <Button
                  variant="outline"
                  className="rounded-l-md rounded-r-none px-2 py-2"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <span className="sr-only">Previous</span>
                  上一頁
                </Button>
                <Button
                  variant="outline"
                  className="rounded-r-md rounded-l-none px-2 py-2"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page * limit >= total}
                >
                  <span className="sr-only">Next</span>
                  下一頁
                </Button>
              </nav>
            </div>
          </div>
        </div>
      </div>
      </>
      )}

      <DriverOrderModal open={showOrderModal} onClose={() => setShowOrderModal(false)} onSaved={fetchDrivers} />

      <DriverDrawer
        isOpen={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false)
          setSelectedDriver(null)
        }}
        onSubmit={selectedDriver ? handleUpdate : handleCreate}
        tenants={tenants}
        driver={selectedDriver}
        defaultTenantId={user?.tenant_id}
      />

      {expensePanelDriver && (
        <DriverExpensePanel
          driverId={expensePanelDriver.id}
          driverName={expensePanelDriver.name}
          isOpen={true}
          onClose={() => setExpensePanelDriver(null)}
        />
      )}

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg max-w-sm w-full p-6 space-y-4">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">確認刪除</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">確定要刪除此司機嗎？此操作無法撤銷。</p>
            <div className="flex gap-3 justify-end pt-4">
              <Button
                variant="outline"
                onClick={() => setShowDeleteConfirm(null)}
                className="px-4 py-2"
              >
                取消
              </Button>
              <Button
                onClick={() => confirmDelete(showDeleteConfirm)}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white"
              >
                刪除
              </Button>
            </div>
          </div>
        </div>
      )}

      {showQuickCreate && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <Zap className="h-5 w-5 text-orange-500" /> 快速新增司機
              </h3>
              <button onClick={() => setShowQuickCreate(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer">✕</button>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">貼上司機資訊</label>
                <button
                  type="button"
                  className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline cursor-pointer"
                  onClick={() => {
                    const tpl = '姓名: 陳OO\n電話: 0912345678\n車號: RAA-0001\n車種: 黑Camry\n駕照:\n駕照到期: 2027-05-01'
                    setQuickText(tpl)
                    setQuickParsed(parseDriverText(tpl))
                    setQuickCreateError('')
                  }}
                >
                  填入範例
                </button>
              </div>
              <textarea
                className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:ring-1 focus:ring-ring resize-none"
                rows={6}
                placeholder={'姓名|電話|車號  或\n司機：陳德志\n電話：0932934185\n車號：RBF-8701\n車種：黑Camry'}
                value={quickText}
                onChange={(e) => {
                  setQuickText(e.target.value)
                  setQuickParsed(e.target.value.trim() ? parseDriverText(e.target.value) : null)
                  setQuickCreateError('')
                }}
              />
            </div>

            {quickParsed && (
              <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-3 space-y-1 text-sm">
                <div className="font-medium text-blue-800 dark:text-blue-300 mb-2">解析結果</div>
                {[
                  ['姓名', quickParsed.name],
                  ['電話', quickParsed.phone],
                  ['車號', quickParsed.vehicle_plate],
                  ['車色', quickParsed.vehicle_color],
                  ['車型', quickParsed.vehicle_brand_model],
                  ['駕照', quickParsed.license_number],
                  ['駕照到期', quickParsed.license_expiry],
                ].map(([label, val]) => val ? (
                  <div key={label as string} className="flex gap-2">
                    <span className="text-gray-500 w-16 shrink-0">{label}</span>
                    <span className="text-gray-900 dark:text-gray-100 font-medium">{val}</span>
                  </div>
                ) : null)}
              </div>
            )}

            {quickCreateError && (
              <p className="text-sm text-red-600">{quickCreateError}</p>
            )}

            <div className="flex gap-3 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowQuickCreate(false)}>取消</Button>
              <Button
                className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                onClick={handleQuickCreate}
                disabled={quickCreateLoading || !quickParsed?.name}
              >
                {quickCreateLoading ? '建立中...' : '建立司機'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
